import { UserLevel } from '@infuse/iam/user/type';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { fetchMoveDestinations, MoveDestinations } from './useMoveDestinations';

export interface DestinationOption {
  key: string;
  value: string;
}

export interface ParsedDestination {
  destinationType: UserLevel;
  destinationId?: string;
  targetTenantId?: string;
}

const DESTINATION_TYPES = {
  DOMAIN: 'domain',
  TENANT: 'tenant',
  DOMAIN_ORGANISATION: 'domain-organisation',
  TENANT_ORGANISATION: 'tenant-organisation'
} as const;

export const useDestinationOptions = (options?: { excludeDomain?: boolean; excludeTenant?: boolean }) => {
  const { t } = useI18n({ useScope: 'global' });
  const loading = ref<boolean>(false);
  const moveDestinations = ref<MoveDestinations>({ organisations: [], tenants: [] });
  const excludeDomain = options?.excludeDomain ?? false;
  const excludeTenant = options?.excludeTenant ?? false;

  // Destination value builders ensures reusability and consistency between building and parsing
  const buildDestinationValue = {
    domain: () => 'domain',
    tenant: (tenantId: string) => `tenant:${tenantId}`,
    organisationDomain: (orgId: string) => `domain-organisation:${orgId}`,
    organisationTenant: (tenantId: string, orgId: string) => `tenant-organisation:${tenantId}:${orgId}`
  };

  const destinationOptions = computed(() => {
    const opts: DestinationOption[] = [];

    // Add domain destination (unless excluded)
    if (!excludeDomain) {
      opts.push({
        key: t('iam.users.domain_destination'),
        value: buildDestinationValue.domain()
      });
    }

    // Add tenants (unless excluded)
    if (!excludeTenant) {
      moveDestinations.value.tenants.forEach((tenant) => {
        opts.push({
          key: `${tenant.name} (Tenant)`,
          value: buildDestinationValue.tenant(tenant.id)
        });
      });
    }

    // Separate organisations by their tenantId
    moveDestinations.value.organisations.forEach((org) => {
      if (!org.tenantId) {
        // Domain-level organisation (tenantId is null)
        opts.push({
          key: `${org.name} (Organisation)`,
          value: buildDestinationValue.organisationDomain(org.id)
        });
      } else {
        // Tenant-level organisation (has tenantId)
        const tenant = moveDestinations.value.tenants.find((t) => t.id === org.tenantId);
        if (tenant) {
          opts.push({
            key: `${org.name} (${tenant.name} Organisation)`,
            value: buildDestinationValue.organisationTenant(org.tenantId, org.id)
          });
        }
      }
    });

    // Sort options with hierarchy: Domain > Tenant > Organisation, then alphabetically within each group
    return opts;
  });

  const fetchDestinations = async () => {
    loading.value = true;
    try {
      moveDestinations.value = await fetchMoveDestinations();
    } catch (error) {
      console.error('Failed to fetch move destinations:', error);
    } finally {
      loading.value = false;
    }
  };

  return {
    destinationOptions,
    fetchDestinations,
    parseDestination,
    loading,
    moveDestinations
  };
};

export function parseDestination(destinationValue: string): ParsedDestination {
  const [type, ...ids] = destinationValue.split(':');

  switch (type) {
    case DESTINATION_TYPES.DOMAIN:
      return {
        destinationType: UserLevel.DOMAIN,
        destinationId: undefined,
        targetTenantId: undefined
      };

    case DESTINATION_TYPES.TENANT:
      if (ids.length < 1) {
        throw new Error('Tenant destination requires tenant ID');
      }
      return {
        destinationType: UserLevel.TENANT,
        destinationId: ids[0],
        targetTenantId: undefined
      };

    case DESTINATION_TYPES.DOMAIN_ORGANISATION:
      if (ids.length < 1) {
        throw new Error('Organisation destination requires organisation ID');
      }
      return {
        destinationType: UserLevel.ORGANISATION,
        destinationId: ids[0],
        targetTenantId: undefined
      };

    case DESTINATION_TYPES.TENANT_ORGANISATION:
      if (ids.length < 2) {
        throw new Error('Tenant organisation destination requires tenant ID and organisation ID');
      }
      return {
        destinationType: UserLevel.ORGANISATION,
        destinationId: ids[1],
        targetTenantId: ids[0]
      };

    default:
      throw new Error(`Unknown destination type: ${type}`);
  }
}
